"use client";

import { IconDownload } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  formatCostUsd,
  type UsageLedgerFilter,
  useUsageBuckets,
  useUsageLedger,
  useUsageSummary,
} from "@/lib/usage/usage.hooks";

const compactNumber = (value: number) =>
  new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value);

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(iso));

const chartConfig = {
  spend: { label: "Spend", color: "var(--chart-1)" },
} satisfies ChartConfig;

const PERIOD_30D = { preset: "last_30d" } as const;

export default function AnalyticsPage() {
  const [modelFilter, setModelFilter] = useState<string>("all");

  const activeFilter: UsageLedgerFilter | null = modelFilter !== "all" ? { model: modelFilter } : null;

  const { data: summaryData, isLoading: summaryLoading } = useUsageSummary(PERIOD_30D);
  const { data: dailyData, isLoading: dailyLoading } = useUsageBuckets({ count: 30 });
  // Unfiltered ledger — drives the model filter buttons (always stable, never changes on filter select).
  const { data: unfilteredLedgerData } = useUsageLedger(200);
  // Filtered ledger — drives the table rows.
  const { data: ledgerData, isLoading: ledgerLoading } = useUsageLedger(200, null, activeFilter);

  const summary = summaryData?.usage_summary;
  const dailyBuckets = useMemo(() => dailyData?.usage_buckets ?? [], [dailyData]);
  const modelRows = useMemo(() => summaryData?.usage_summary?.by_model ?? [], [summaryData]);
  const ledgerEvents = useMemo(() => ledgerData?.usage_ledger?.events ?? [], [ledgerData]);

  const modelNames = useMemo(
    () => Array.from(new Set((unfilteredLedgerData?.usage_ledger?.events ?? []).map((e) => e.model))).sort(),
    [unfilteredLedgerData],
  );

  const dailySpendLine = useMemo(
    () =>
      dailyBuckets.map((b) => ({
        label: new Intl.DateTimeFormat("en-US", { month: "numeric", day: "numeric" }).format(
          new Date(`${b.date}T00:00:00`),
        ),
        spend: Number(b.cost_cents) / 100,
      })),
    [dailyBuckets],
  );

  const isLoading = summaryLoading || dailyLoading || ledgerLoading;

  const handleExportCsv = () => {
    const header = ["Date", "Model", "Input tokens", "Output tokens", "Cost (USD)", "API key"].join(",");
    const csv = [
      header,
      ...ledgerEvents.map((e) =>
        [
          `"${formatDate(e.occurred_at)}"`,
          `"${e.model}"`,
          e.input_tokens,
          e.output_tokens,
          `"${formatCostUsd(e.cost_cents)}"`,
          `"${e.api_key_label ?? "—"}"`,
        ].join(","),
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "usage.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="@container/main flex flex-1 flex-col gap-2">
      <div className="flex flex-col gap-4 md:gap-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Usage</h1>
            <p className="text-muted-foreground">
              Monitor API spend, token consumption, and per-model usage across your organization.
            </p>
          </div>
          <Button variant="outline" className="shrink-0" onClick={handleExportCsv} disabled={isLoading}>
            <IconDownload className="mr-2 size-4" />
            Export CSV
          </Button>
        </div>

        {/* Summary cards */}
        <div>
          <h2 className="mb-3 text-sm font-medium text-muted-foreground">Summary (last 30 days)</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Estimated spend</CardTitle>
              </CardHeader>
              <CardContent>
                {summaryLoading ? (
                  <p className="text-muted-foreground text-sm">Loading...</p>
                ) : (
                  <p className="text-2xl font-semibold">{formatCostUsd(summary?.estimated_cost_cents ?? 0)}</p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total input tokens</CardTitle>
              </CardHeader>
              <CardContent>
                {summaryLoading ? (
                  <p className="text-muted-foreground text-sm">Loading...</p>
                ) : (
                  <p className="text-2xl font-semibold">{compactNumber(Number(summary?.input_tokens ?? 0))}</p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total output tokens</CardTitle>
              </CardHeader>
              <CardContent>
                {summaryLoading ? (
                  <p className="text-muted-foreground text-sm">Loading...</p>
                ) : (
                  <p className="text-2xl font-semibold">{compactNumber(Number(summary?.output_tokens ?? 0))}</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Per-model breakdown */}
        <div>
          <h2 className="mb-3 text-sm font-medium text-muted-foreground">Per-model breakdown</h2>
          {summaryLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : modelRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No usage yet.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[...modelRows]
                .sort((a, b) => Number(b.cost_cents) - Number(a.cost_cents))
                .map((row) => (
                  <Card key={row.model}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base font-semibold">{row.model}</CardTitle>
                      <CardDescription>
                        {row.requests} {Number(row.requests) === 1 ? "request" : "requests"}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xl font-semibold">{formatCostUsd(row.cost_cents)}</p>
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}
        </div>

        {/* Daily spend chart */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Daily spend</CardTitle>
              <CardDescription>Last 30 days.</CardDescription>
            </CardHeader>
            <CardContent className="pl-0">
              {dailyLoading ? (
                <p className="py-10 text-center text-sm text-muted-foreground">Loading...</p>
              ) : (
                <ChartContainer config={chartConfig} className="aspect-auto h-[240px] w-full">
                  <LineChart data={dailySpendLine} margin={{ left: 12, right: 12 }}>
                    <CartesianGrid vertical={false} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={16} />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      tickFormatter={(v) => `$${Number(v).toFixed(0)}`}
                    />
                    <ChartTooltip
                      cursor={{ strokeDasharray: "4 4" }}
                      content={<ChartTooltipContent formatter={(v) => [`$${Number(v).toFixed(2)}`, "Spend"]} />}
                    />
                    <Line type="monotone" dataKey="spend" stroke="var(--color-spend)" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Transaction log */}
        <div>
          <h2 className="mb-3 text-sm font-medium text-muted-foreground">Transaction log</h2>
          <div className="mb-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={modelFilter === "all" ? "secondary" : "outline"}
              onClick={() => setModelFilter("all")}
            >
              All
            </Button>
            {modelNames.map((name) => (
              <Button
                key={name}
                size="sm"
                variant={modelFilter === name ? "secondary" : "outline"}
                className="max-w-[200px] truncate"
                onClick={() => setModelFilter(name)}
              >
                {name}
              </Button>
            ))}
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead className="text-right">Input tokens</TableHead>
                  <TableHead className="text-right">Output tokens</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead>API key</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledgerLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                      Loading usage...
                    </TableCell>
                  </TableRow>
                ) : ledgerEvents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                      No usage records yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  ledgerEvents.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="whitespace-nowrap">{formatDate(e.occurred_at)}</TableCell>
                      <TableCell className="max-w-[220px] truncate font-mono text-xs">{e.model}</TableCell>
                      <TableCell className="text-right tabular-nums">{compactNumber(e.input_tokens)}</TableCell>
                      <TableCell className="text-right tabular-nums">{compactNumber(e.output_tokens)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCostUsd(e.cost_cents)}</TableCell>
                      <TableCell className="max-w-[180px] truncate font-mono text-xs">
                        {e.api_key_label ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
